import { useState } from 'react';
import { useLocale } from '@platform/core';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';

export const LoginPage: React.FC = () => {
  const { signInWithEmail, signInWithGoogle, isLoading } = useSimpleAuth();
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmail(email, password);
    } catch (err: any) {
      setError(err.message || t('errors.loginFailed'));
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || t('errors.loginFailed'));
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <span className="logo">🔑</span>
          <h1>TurnKeyOS<span style={{color:'#3b82f6'}}>.AI</span></h1>
          <p style={{fontSize:'14px',color:'#64748b',marginTop:'4px'}}>Budget</p>
          <p>{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <input
            type="email"
            placeholder={t('login.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />

          <input
            type="password"
            placeholder={t('login.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />

          <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
            {isLoading ? t('general.loading') : t('login.signIn')}
          </button>
        </form>

        <div className="divider"><span>{t('login.or')}</span></div>

        <button className="btn btn-google btn-full" onClick={handleGoogleLogin} disabled={isLoading}>
          <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginInlineEnd: '0.5rem' }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('login.continueWithGoogle')}
        </button>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #22c55e 0%, #3b82f6 100%);
          padding: 1rem;
        }

        .login-container {
          width: 100%;
          max-width: 400px;
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .logo {
          font-size: 3rem;
          display: block;
          margin-bottom: 0.5rem;
        }

        .login-header h1 {
          margin: 0;
          font-size: 1.5rem;
        }

        .login-header p {
          color: var(--color-text-secondary);
          margin: 0.5rem 0 0;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .login-form input {
          padding: 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 1rem;
        }

        .error-message {
          background: #fee2e2;
          color: #991b1b;
          padding: 0.75rem;
          border-radius: var(--radius);
          font-size: 0.875rem;
          text-align: center;
        }

        .btn-full { width: 100%; }

        .divider {
          display: flex;
          align-items: center;
          margin: 1.5rem 0;
        }

        .divider::before, .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--color-border);
        }

        .divider span {
          padding: 0 1rem;
          color: var(--color-text-secondary);
          font-size: 0.875rem;
        }

        .btn-google {
          background: white;
          border: 1px solid var(--color-border);
        }
      `}</style>
    </div>
  );
};
